<script setup>
import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useCrmStore } from 'dashboard/stores/crm';
import { useStoreGetters, useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import crmApi from 'dashboard/api/crm';

const emit = defineEmits(['close']);
const dialogRef = ref(null);
const { t } = useI18n();
const crmStore = useCrmStore();
const store = useStore();
const getters = useStoreGetters();

const allAgents = computed(() => getters['agents/getAgents'].value ?? []);

// Local copy of funnels for management
const localFunnels = ref([]);
const isLoading = ref(false);

// Expanded funnel state
const expandedFunnelId = ref(null);
const funnelStages = ref({}); // { [funnelId]: stage[] }
const funnelAgents = ref({}); // { [funnelId]: agent[] }
const loadingFunnelId = ref(null);

// Edit funnel name inline
const editingFunnelId = ref(null);
const editingFunnelName = ref('');

// New stage per funnel
const newStageName = ref({}); // { [funnelId]: string }
const newStageColor = ref({}); // { [funnelId]: string }

// Edit stage inline
const editingStageId = ref(null);
const editingStageName = ref('');

// Create new funnel form
const showCreateForm = ref(false);
const newFunnelName = ref('');
const newFunnelStages = ref([]);
const newFunnelStageName = ref('');
const newFunnelStageColor = ref('#6366f1');
const newFunnelAgentIds = ref([]);
const isCreating = ref(false);

// Origins
const origins = ref([]);
const isLoadingOrigins = ref(false);
const editingOriginId = ref(null);
const editingOriginName = ref('');
const newOriginName = ref('');

const loadOrigins = async () => {
  isLoadingOrigins.value = true;
  try {
    const { data } = await crmApi.getOrigins();
    origins.value = data;
  } catch {
    useAlert(t('CRM.SETTINGS.SAVE_FAILED'));
  } finally {
    isLoadingOrigins.value = false;
  }
};

const startEditOrigin = origin => {
  editingOriginId.value = origin.id;
  editingOriginName.value = origin.name;
};

const cancelEditOrigin = () => {
  editingOriginId.value = null;
  editingOriginName.value = '';
};

const saveOrigin = async origin => {
  if (!editingOriginName.value.trim()) return;
  try {
    await crmApi.updateOrigin(origin.id, {
      name: editingOriginName.value.trim(),
    });
    origin.name = editingOriginName.value.trim();
    cancelEditOrigin();
  } catch {
    useAlert(t('CRM.SETTINGS.SAVE_FAILED'));
  }
};

const toggleOriginActive = async origin => {
  const next = !origin.active;
  try {
    await crmApi.updateOrigin(origin.id, { active: next });
    origin.active = next;
  } catch {
    useAlert(t('CRM.SETTINGS.SAVE_FAILED'));
  }
};

const addOrigin = async () => {
  if (!newOriginName.value.trim()) return;
  try {
    const { data } = await crmApi.createOrigin({
      name: newOriginName.value.trim(),
      position: origins.value.length,
    });
    origins.value.push(data);
    newOriginName.value = '';
  } catch {
    useAlert(t('CRM.SETTINGS.SAVE_FAILED'));
  }
};

const deleteOrigin = async origin => {
  try {
    await crmApi.deleteOrigin(origin.id);
    origins.value = origins.value.filter(o => o.id !== origin.id);
  } catch (err) {
    const msg =
      err?.response?.data?.error || t('CRM.SETTINGS.ORIGIN_DELETE_ERROR');
    useAlert(msg);
  }
};

const load = async () => {
  isLoading.value = true;
  try {
    const { data } = await crmApi.getFunnels();
    localFunnels.value = data.payload || data;
  } finally {
    isLoading.value = false;
  }
};

onMounted(() => {
  dialogRef.value?.open();
  store.dispatch('agents/get');
  load();
  loadOrigins();
});

// --- Funnel expand ---
const toggleExpand = async funnelId => {
  if (expandedFunnelId.value === funnelId) {
    expandedFunnelId.value = null;
    return;
  }
  expandedFunnelId.value = funnelId;
  if (!funnelStages.value[funnelId]) {
    loadingFunnelId.value = funnelId;
    try {
      const [stagesRes, agentsRes] = await Promise.all([
        crmApi.getStages(funnelId),
        crmApi.getFunnelAgents(funnelId),
      ]);
      funnelStages.value[funnelId] = stagesRes.data.payload || stagesRes.data;
      funnelAgents.value[funnelId] = (
        agentsRes.data.payload || agentsRes.data
      ).map(a => a.id ?? a.user_id);
    } finally {
      loadingFunnelId.value = null;
    }
  }
};

// --- Edit funnel name ---
const startEditFunnel = funnel => {
  editingFunnelId.value = funnel.id;
  editingFunnelName.value = funnel.name;
};

const saveEditFunnel = async funnelId => {
  const name = editingFunnelName.value.trim();
  if (!name) return;
  await crmApi.updateFunnel(funnelId, { name });
  const f = localFunnels.value.find(x => x.id === funnelId);
  if (f) f.name = name;
  const sf = crmStore.funnels.find(x => x.id === funnelId);
  if (sf) sf.name = name;
  editingFunnelId.value = null;
};

const cancelEditFunnel = () => {
  editingFunnelId.value = null;
};

// --- Toggle funnel active/inactive ---
const toggleFunnelActive = async funnel => {
  const newActive = !funnel.active;
  await crmApi.updateFunnel(funnel.id, { active: newActive });
  funnel.active = newActive;
  const sf = crmStore.funnels.find(f => f.id === funnel.id);
  if (sf) sf.active = newActive;
  if (!newActive && crmStore.currentFunnelId === funnel.id) {
    const nextActive = crmStore.funnels.find(
      f => f.active && f.id !== funnel.id
    );
    if (nextActive) await crmStore.selectFunnel(nextActive.id);
    else {
      crmStore.currentFunnelId = null;
      crmStore.stages = [];
      crmStore.opportunities = [];
    }
  }
};

// --- Stages ---
const addStage = async funnelId => {
  const name = (newStageName.value[funnelId] ?? '').trim();
  if (!name) return;
  const color = newStageColor.value[funnelId] ?? '#6366f1';
  const stages = funnelStages.value[funnelId] ?? [];
  const { data } = await crmApi.createStage(funnelId, {
    name,
    color,
    position: stages.length,
  });
  const stage = data.payload || data;
  funnelStages.value[funnelId] = [...stages, stage];
  newStageName.value[funnelId] = '';
  newStageColor.value[funnelId] = '#6366f1';
  if (funnelId === crmStore.currentFunnelId)
    await crmStore.fetchStages(funnelId);
};

const startEditStage = stage => {
  editingStageId.value = stage.id;
  editingStageName.value = stage.name;
};

const saveEditStage = async (funnelId, stage) => {
  const name = editingStageName.value.trim();
  if (!name) return;
  await crmApi.updateStage(funnelId, stage.id, { name, color: stage.color });
  stage.name = name;
  if (funnelId === crmStore.currentFunnelId)
    await crmStore.fetchStages(funnelId);
  editingStageId.value = null;
};

const deleteStage = async (funnelId, stageId) => {
  try {
    await crmApi.deleteStage(funnelId, stageId);
    funnelStages.value[funnelId] = funnelStages.value[funnelId].filter(
      s => s.id !== stageId
    );
    if (funnelId === crmStore.currentFunnelId)
      await crmStore.fetchStages(funnelId);
  } catch (e) {
    const msg = e?.response?.data?.error;
    useAlert(msg || t('CRM.SETTINGS.STAGE_DELETE_ERROR'));
  }
};

// --- Agents ---
const isFunnelAgent = (funnelId, agentId) =>
  (funnelAgents.value[funnelId] ?? []).includes(agentId);

const toggleFunnelAgent = async (funnelId, agentId) => {
  if (isFunnelAgent(funnelId, agentId)) {
    await crmApi.removeFunnelAgent(funnelId, agentId);
    funnelAgents.value[funnelId] = funnelAgents.value[funnelId].filter(
      id => id !== agentId
    );
  } else {
    await crmApi.addFunnelAgent(funnelId, agentId);
    funnelAgents.value[funnelId] = [
      ...(funnelAgents.value[funnelId] ?? []),
      agentId,
    ];
  }
};

// --- Create new funnel ---
const addNewFunnelStage = () => {
  const name = newFunnelStageName.value.trim();
  if (!name) return;
  newFunnelStages.value.push({ name, color: newFunnelStageColor.value });
  newFunnelStageName.value = '';
  newFunnelStageColor.value = '#6366f1';
};

const toggleNewFunnelAgent = agentId => {
  const idx = newFunnelAgentIds.value.indexOf(agentId);
  if (idx === -1) newFunnelAgentIds.value.push(agentId);
  else newFunnelAgentIds.value.splice(idx, 1);
};

const handleCreate = async () => {
  if (!newFunnelName.value.trim()) return;
  isCreating.value = true;
  try {
    const { data } = await crmApi.createFunnel({
      name: newFunnelName.value.trim(),
    });
    const funnel = data.payload || data;
    await Promise.all(
      newFunnelStages.value.map((s, i) =>
        crmApi.createStage(funnel.id, {
          name: s.name,
          color: s.color,
          position: i,
        })
      )
    );
    await Promise.all(
      newFunnelAgentIds.value.map(userId =>
        crmApi.addFunnelAgent(funnel.id, userId)
      )
    );
    crmStore.funnels.push(funnel);
    localFunnels.value.push(funnel);
    await crmStore.selectFunnel(funnel.id);
    newFunnelName.value = '';
    newFunnelStages.value = [];
    newFunnelAgentIds.value = [];
    showCreateForm.value = false;
  } finally {
    isCreating.value = false;
  }
};

const handleClose = () => emit('close');
</script>

<template>
  <Dialog
    ref="dialogRef"
    :title="t('CRM.SETTINGS.TITLE')"
    width="lg"
    @close="handleClose"
  >
    <template #footer>
      <div class="flex justify-end">
        <button
          class="px-4 py-2 text-sm rounded bg-n-slate-3 hover:bg-n-slate-4 text-n-slate-12 transition-colors"
          @click="handleClose"
        >
          {{ t('CRM.SETTINGS.CLOSE') }}
        </button>
      </div>
    </template>

    <div class="flex flex-col gap-4 min-h-[200px]">
      <!-- Loading -->
      <p v-if="isLoading" class="text-sm text-n-slate-10 text-center py-4">
        {{ t('CRM.SETTINGS.LOADING') }}
      </p>

      <template v-else>
        <!-- Origins section -->
        <div class="mb-6">
          <h3
            class="text-sm font-semibold text-slate-700 dark:text-slate-200 mb-3"
          >
            {{ t('CRM.SETTINGS.ORIGINS') }}
          </h3>

          <div v-if="isLoadingOrigins" class="text-xs text-slate-500">
            {{ t('CRM.SETTINGS.LOADING') }}
          </div>

          <div v-else>
            <div
              v-for="origin in origins"
              :key="origin.id"
              class="flex items-center gap-2 py-1.5 border-b border-slate-100 dark:border-slate-700"
            >
              <!-- Editing mode -->
              <template v-if="editingOriginId === origin.id">
                <input
                  v-model="editingOriginName"
                  class="flex-1 text-sm border rounded px-2 py-0.5 dark:bg-slate-800 dark:text-slate-100"
                  @keyup.enter="saveOrigin(origin)"
                  @keyup.escape="cancelEditOrigin"
                />
                <button
                  class="text-xs text-green-600 hover:underline"
                  @click="saveOrigin(origin)"
                >
                  {{ t('CRM.SETTINGS.SAVE') }}
                </button>
                <button
                  class="text-xs text-slate-500 hover:underline"
                  @click="cancelEditOrigin"
                >
                  {{ t('CRM.SETTINGS.CANCEL_BTN') }}
                </button>
              </template>

              <!-- View mode -->
              <template v-else>
                <span
                  class="flex-1 text-sm"
                  :class="
                    origin.active
                      ? 'text-slate-800 dark:text-slate-100'
                      : 'text-slate-400 line-through'
                  "
                >
                  {{ origin.name }}
                </span>
                <span
                  v-if="!origin.active"
                  class="text-xs text-slate-400 italic"
                >
                  {{ t('CRM.SETTINGS.ORIGIN_DEACTIVATED') }}
                </span>
                <button
                  class="text-xs text-blue-600 hover:underline"
                  @click="startEditOrigin(origin)"
                >
                  {{ t('CRM.SETTINGS.EDIT') }}
                </button>
                <button
                  class="text-xs hover:underline"
                  :class="origin.active ? 'text-amber-600' : 'text-green-600'"
                  @click="toggleOriginActive(origin)"
                >
                  {{
                    origin.active
                      ? t('CRM.SETTINGS.DEACTIVATE')
                      : t('CRM.SETTINGS.ACTIVATE')
                  }}
                </button>
                <button
                  class="text-xs text-red-500 hover:underline"
                  @click="deleteOrigin(origin)"
                >
                  {{ t('CRM.SETTINGS.DELETE') }}
                </button>
              </template>
            </div>

            <!-- Add new origin -->
            <div class="flex items-center gap-2 mt-2">
              <input
                v-model="newOriginName"
                :placeholder="t('CRM.SETTINGS.ORIGIN_PLACEHOLDER')"
                class="flex-1 text-sm border rounded px-2 py-0.5 dark:bg-slate-800 dark:text-slate-100"
                @keyup.enter="addOrigin"
              />
              <button
                class="text-xs text-blue-600 hover:underline"
                @click="addOrigin"
              >
                {{ t('CRM.SETTINGS.ADD') }}
              </button>
            </div>
          </div>
        </div>

        <!-- Existing funnels -->
        <div v-if="localFunnels.length > 0" class="flex flex-col gap-2">
          <label
            class="text-xs font-semibold text-n-slate-11 uppercase tracking-wider"
          >
            {{ t('CRM.SETTINGS.TAB_FUNNELS') }}
          </label>

          <div
            v-for="funnel in localFunnels"
            :key="funnel.id"
            class="border border-n-weak rounded overflow-hidden"
          >
            <!-- Funnel header row -->
            <div class="flex items-center gap-2 px-3 py-2 bg-n-slate-2">
              <!-- Inline name edit or display -->
              <template v-if="editingFunnelId === funnel.id">
                <input
                  v-model="editingFunnelName"
                  class="flex-1 border border-n-weak rounded px-2 py-1 text-sm bg-n-surface-1 text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-woot-500"
                  @keydown.enter="saveEditFunnel(funnel.id)"
                  @keydown.esc="cancelEditFunnel"
                />
                <button
                  class="text-xs text-woot-500 hover:text-woot-600 font-medium"
                  @click="saveEditFunnel(funnel.id)"
                >
                  {{ t('CRM.SETTINGS.SAVE') }}
                </button>
                <button
                  class="text-xs text-n-slate-10 hover:text-n-slate-12"
                  @click="cancelEditFunnel"
                >
                  {{ t('CRM.SETTINGS.CANCEL_BTN') }}
                </button>
              </template>
              <template v-else>
                <span
                  class="flex-1 text-sm font-medium"
                  :class="
                    funnel.active
                      ? 'text-n-slate-12'
                      : 'text-n-slate-9 line-through'
                  "
                >
                  {{ funnel.name }}
                </span>
                <span
                  v-if="!funnel.active"
                  class="text-xs bg-n-slate-3 text-n-slate-10 px-1.5 py-0.5 rounded"
                >
                  {{ t('CRM.SETTINGS.INACTIVE') }}
                </span>
                <button
                  class="text-xs text-n-slate-10 hover:text-n-slate-12 transition-colors"
                  @click="startEditFunnel(funnel)"
                >
                  {{ t('CRM.SETTINGS.EDIT') }}
                </button>
                <button
                  class="text-xs transition-colors"
                  :class="
                    funnel.active
                      ? 'text-n-amber-11 hover:text-n-amber-12'
                      : 'text-woot-500 hover:text-woot-600'
                  "
                  @click="toggleFunnelActive(funnel)"
                >
                  {{
                    funnel.active
                      ? t('CRM.SETTINGS.DEACTIVATE')
                      : t('CRM.SETTINGS.ACTIVATE')
                  }}
                </button>
                <button
                  class="text-xs text-n-slate-10 hover:text-n-slate-12 transition-colors ml-1"
                  @click="toggleExpand(funnel.id)"
                >
                  {{ expandedFunnelId === funnel.id ? '▲' : '▼' }}
                </button>
              </template>
            </div>

            <!-- Expanded: Stages + Agents -->
            <div
              v-if="expandedFunnelId === funnel.id"
              class="flex flex-col gap-4 px-3 py-3 bg-n-surface-1"
            >
              <p
                v-if="loadingFunnelId === funnel.id"
                class="text-xs text-n-slate-10"
              >
                {{ t('CRM.SETTINGS.LOADING') }}
              </p>
              <template v-else>
                <!-- Stages -->
                <div class="flex flex-col gap-2">
                  <label
                    class="text-xs font-semibold text-n-slate-11 uppercase tracking-wider"
                  >
                    {{ t('CRM.SETTINGS.TAB_STAGES') }}
                  </label>
                  <div
                    v-if="(funnelStages[funnel.id] ?? []).length > 0"
                    class="flex flex-col gap-1"
                  >
                    <div
                      v-for="stage in funnelStages[funnel.id]"
                      :key="stage.id"
                      class="flex items-center gap-2 px-2 py-1.5 bg-n-slate-2 rounded"
                    >
                      <span
                        class="w-3 h-3 rounded-full flex-shrink-0"
                        :style="{ backgroundColor: stage.color }"
                      />
                      <template v-if="editingStageId === stage.id">
                        <input
                          v-model="editingStageName"
                          class="flex-1 border border-n-weak rounded px-2 py-0.5 text-xs bg-n-surface-1 focus:outline-none focus:ring-1 focus:ring-woot-500"
                          @keydown.enter="saveEditStage(funnel.id, stage)"
                          @keydown.esc="editingStageId = null"
                        />
                        <button
                          class="text-xs text-woot-500 hover:text-woot-600"
                          @click="saveEditStage(funnel.id, stage)"
                        >
                          {{ t('CRM.SETTINGS.SAVE') }}
                        </button>
                        <button
                          class="text-xs text-n-slate-10"
                          @click="editingStageId = null"
                        >
                          {{ t('CRM.SETTINGS.CANCEL_BTN') }}
                        </button>
                      </template>
                      <template v-else>
                        <span class="flex-1 text-xs text-n-slate-12">{{
                          stage.name
                        }}</span>
                        <button
                          class="text-xs text-n-slate-10 hover:text-n-slate-12"
                          @click="startEditStage(stage)"
                        >
                          {{ t('CRM.SETTINGS.EDIT') }}
                        </button>
                        <button
                          class="text-xs text-n-ruby-11 hover:text-n-ruby-12"
                          @click="deleteStage(funnel.id, stage.id)"
                        >
                          {{ t('CRM.SETTINGS.DELETE') }}
                        </button>
                      </template>
                    </div>
                  </div>
                  <!-- Add stage row -->
                  <div class="flex items-center gap-2">
                    <input
                      v-model="newStageColor[funnel.id]"
                      type="color"
                      class="w-7 h-7 rounded cursor-pointer border-0 p-0 flex-shrink-0"
                    />
                    <input
                      v-model="newStageName[funnel.id]"
                      type="text"
                      :placeholder="t('CRM.SETTINGS.STAGE_PLACEHOLDER')"
                      class="flex-1 border border-n-weak rounded px-2 py-1.5 text-xs bg-n-surface-1 text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-woot-500"
                      @keydown.enter="addStage(funnel.id)"
                    />
                    <button
                      :disabled="!(newStageName[funnel.id] ?? '').trim()"
                      class="text-xs bg-n-slate-3 hover:bg-n-slate-4 disabled:opacity-50 text-n-slate-12 px-2 py-1.5 rounded"
                      @click="addStage(funnel.id)"
                    >
                      {{ t('CRM.SETTINGS.ADD') }}
                    </button>
                  </div>
                </div>

                <div class="border-t border-n-weak" />

                <!-- Agents -->
                <div class="flex flex-col gap-2">
                  <label
                    class="text-xs font-semibold text-n-slate-11 uppercase tracking-wider"
                  >
                    {{ t('CRM.SETTINGS.TAB_AGENTS') }}
                  </label>
                  <div class="flex flex-col gap-1 max-h-36 overflow-y-auto">
                    <label
                      v-for="agent in allAgents"
                      :key="agent.id"
                      class="flex items-center gap-2 px-2 py-1.5 rounded cursor-pointer hover:bg-n-slate-2 transition-colors"
                    >
                      <input
                        type="checkbox"
                        :checked="isFunnelAgent(funnel.id, agent.id)"
                        class="accent-woot-500"
                        @change="toggleFunnelAgent(funnel.id, agent.id)"
                      />
                      <span class="text-xs text-n-slate-12">{{
                        agent.name
                      }}</span>
                    </label>
                  </div>
                </div>
              </template>
            </div>
          </div>
        </div>

        <div class="border-t border-n-weak" />

        <!-- Create new funnel -->
        <div class="flex flex-col gap-3">
          <button
            v-if="!showCreateForm"
            class="text-sm text-woot-500 hover:text-woot-600 font-medium text-left transition-colors"
            @click="showCreateForm = true"
          >
            + {{ t('CRM.SETTINGS.NEW_FUNNEL') }}
          </button>

          <template v-else>
            <label
              class="text-xs font-semibold text-n-slate-11 uppercase tracking-wider"
            >
              {{ t('CRM.SETTINGS.NEW_FUNNEL') }}
            </label>

            <input
              v-model="newFunnelName"
              type="text"
              :placeholder="t('CRM.SETTINGS.FUNNEL_PLACEHOLDER')"
              class="border border-n-weak rounded px-3 py-2 text-sm text-n-slate-12 bg-n-surface-1 focus:outline-none focus:ring-2 focus:ring-woot-500"
            />

            <!-- Stages for new funnel -->
            <div class="flex flex-col gap-1">
              <label class="text-xs text-n-slate-11">{{
                t('CRM.SETTINGS.TAB_STAGES')
              }}</label>
              <div
                v-if="newFunnelStages.length > 0"
                class="flex flex-col gap-1 mb-1"
              >
                <div
                  v-for="(s, i) in newFunnelStages"
                  :key="i"
                  class="flex items-center gap-2 px-2 py-1 bg-n-slate-2 rounded"
                >
                  <span
                    class="w-3 h-3 rounded-full"
                    :style="{ backgroundColor: s.color }"
                  />
                  <span class="flex-1 text-xs text-n-slate-12">{{
                    s.name
                  }}</span>
                  <button
                    class="text-xs text-n-ruby-11"
                    @click="newFunnelStages.splice(i, 1)"
                  >
                    {{ t('CRM.SETTINGS.REMOVE') }}
                  </button>
                </div>
              </div>
              <div class="flex items-center gap-2">
                <input
                  v-model="newFunnelStageColor"
                  type="color"
                  class="w-7 h-7 rounded border-0 p-0 cursor-pointer flex-shrink-0"
                />
                <input
                  v-model="newFunnelStageName"
                  type="text"
                  :placeholder="t('CRM.SETTINGS.STAGE_PLACEHOLDER')"
                  class="flex-1 border border-n-weak rounded px-2 py-1.5 text-xs bg-n-surface-1 text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-woot-500"
                  @keydown.enter="addNewFunnelStage"
                />
                <button
                  :disabled="!newFunnelStageName.trim()"
                  class="text-xs bg-n-slate-3 hover:bg-n-slate-4 disabled:opacity-50 px-2 py-1.5 rounded"
                  @click="addNewFunnelStage"
                >
                  {{ t('CRM.SETTINGS.ADD') }}
                </button>
              </div>
            </div>

            <!-- Agents for new funnel -->
            <div class="flex flex-col gap-1">
              <label class="text-xs text-n-slate-11">{{
                t('CRM.SETTINGS.TAB_AGENTS')
              }}</label>
              <div class="flex flex-col gap-1 max-h-32 overflow-y-auto">
                <label
                  v-for="agent in allAgents"
                  :key="agent.id"
                  class="flex items-center gap-2 px-2 py-1 rounded cursor-pointer hover:bg-n-slate-2"
                >
                  <input
                    type="checkbox"
                    :checked="newFunnelAgentIds.includes(agent.id)"
                    class="accent-woot-500"
                    @change="toggleNewFunnelAgent(agent.id)"
                  />
                  <span class="text-xs text-n-slate-12">{{ agent.name }}</span>
                </label>
              </div>
            </div>

            <div class="flex gap-2">
              <button
                :disabled="!newFunnelName.trim() || isCreating"
                class="px-4 py-2 text-sm rounded bg-woot-500 hover:bg-woot-600 disabled:opacity-50 text-white font-medium transition-colors"
                @click="handleCreate"
              >
                {{
                  isCreating
                    ? t('CRM.SETTINGS.LOADING')
                    : t('CRM.SETTINGS.CREATE')
                }}
              </button>
              <button
                class="px-4 py-2 text-sm rounded bg-n-slate-3 hover:bg-n-slate-4 text-n-slate-12 transition-colors"
                @click="showCreateForm = false"
              >
                {{ t('CRM.SETTINGS.CANCEL_BTN') }}
              </button>
            </div>
          </template>
        </div>
      </template>
    </div>
  </Dialog>
</template>
